import { useCallback, useMemo, useRef, useState } from "react";
import type { InputBarConversationController } from "@/components/panels/input-bar-types";
import { prepareFrameNewConversation } from "@/lib/model-switch-conversation";
import { translate } from "@/i18n";
import { framesConversationMessages } from "@/i18n/messages/frames-conversation";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { useNotificationStore } from "@/stores/notification-store";

/** New chat replaces this frame's chat; voice and the standalone input follow
 *  it, since the user is switching to it. */
export function useConversationFrameNewChat(
  controller: InputBarConversationController,
  frameId: string,
  agentId: string | undefined,
) {
  const pendingRef = useRef<Promise<string | undefined> | null>(null);
  const [creating, setCreating] = useState(false);
  const createConversation = useCallback((): Promise<string | undefined> => {
    if (pendingRef.current) return pendingRef.current;
    if (!controller.canStartNewConversation || !agentId) {
      return Promise.resolve(undefined);
    }
    controller.onNewConversation();
    setCreating(true);
    const pending = (async () => {
      try {
        const conversationId = await prepareFrameNewConversation(
          agentId,
          frameId,
        )();
        controller.onClear();
        return conversationId;
      } catch (error) {
        controller.onClear();
        useNotificationStore.getState().addSystemToast({
          id: `conversation-frame-new-chat:${frameId}`,
          level: "error",
          title: translate(framesConversationMessages, "newChatFailed"),
          description:
            error instanceof Error
              ? error.message
              : translate(framesSharedMessages, "pleaseTryAgain"),
        });
        return undefined;
      } finally {
        pendingRef.current = null;
        setCreating(false);
      }
    })();
    pendingRef.current = pending;
    return pending;
  }, [agentId, controller, frameId]);
  const conversationController = useMemo<InputBarConversationController>(
    () => ({
      ...controller,
      onNewConversation: async () => Boolean(await createConversation()),
    }),
    [controller, createConversation],
  );
  return { conversationController, createConversation, creating };
}
